import i18n, { EN } from '@/assets/js/i18n';
import { formatterColTransferUnit } from '@/utils/enterprise';
import { computed } from 'vue';
import { useStore } from '@/utils/vue-api';

/**
 * 股票报价相关的通用方法
 */
export function useStockVO() {
	const store = useStore();
	const isHKStock = computed(() => store.getters.isHKStock);

	const formattedValue = (value, format = null) => {
		if (value === null || value === undefined) return '- -';
		if (typeof value !== 'number') return '- -';
		if (isNaN(value)) return '- -';
		return format ? format(value) : value;
	};

	const stockColTransferUnit = (amount, split = false) => {
		if (typeof amount !== 'number') return '- -';
		return formatterColTransferUnit(amount, split);
	};

	const ttmFilter = (amount, $t) => {
		if (amount < 0) {
			return i18n.t('enterprise.loss');
		} else {
			return stockColTransferUnit(amount);
		}
	};

	const computedClosePrice = data => {
		let res = stockColTransferUnit(data, true);
		return `${res[0]}<span style="font-size: 0.2rem;">${res[1]}</span>`;
	};

	// Methods
	const divideByHundred = value => {
		if (typeof value !== 'number') {
			return '-';
		}
		return value.toFixed(2);
	};

	const appendPlusIfPositive = value => {
		if (typeof value !== 'number') {
			return '-';
		}
		return value > 0 ? '+' + value.toString() : value;
	};

	const formatChangePct = value => {
		if (typeof value !== 'number') {
			return '-';
		}
		let _value = value.toFixed(2);
		_value = _value > 0 ? '+' + _value.toString() : _value;
		return `${_value}%`;
	};

	const getVolumeAmountAndUnit = (volume, useHkShares = false) => {
		// turnoverVolumeShares - 股
		// turnoverVolume - 手

		if (useHkShares) {
			return {
				amount: volume,
				unit: i18n.locale === EN ? '' : '股'
			};
		}

		return {
			amount: i18n.locale === EN ? volume * 100 : volume,
			unit: i18n.locale === EN ? '' : '手'
		};
	};
	const computedVolume = (data, market) => {
		// 优先使用传入的 market 参数判断，无 market 时使用 store getter
		const isHK = market ? market === 'hk' : isHKStock.value;
		const volumeField = isHK ? 'turnoverVolumeShares' : 'turnoverVolume';
		const volumeValue = data[volumeField];

		const { amount, unit } = getVolumeAmountAndUnit(volumeValue, isHK);
		const value = stockColTransferUnit(amount);

		return `${value}${unit}`;
	};

	/**
	 * 计算成交量 - SSE数据专用版本
	 *
	 * 适用场景：上交所SSE实时数据（loadSseQuoteData）
	 *
	 * 数据特点：
	 * - SSE接口返回的成交量（turnoverVolume）以"股"为单位
	 * - 需要转换为A股标准单位"手"（1手=100股）
	 *
	 * 处理逻辑：
	 * - 英文环境：使用标准格式化（与 computedVolume 一致）
	 * - 中文环境：直接 /100 转换为"手"，不进行万/亿单位转换
	 *
	 * @param {Object} data - 股票数据对象
	 * @returns {string|number} - 格式化后的成交量字符串或数字
	 */
	const computedVolumeForSse = data => {
		// 优先使用传入的 market 参数判断，无 market 时使用 store getter
		const volumeField = 'turnoverVolume';
		const volumeValue = data[volumeField];

		const { amount, unit } = getVolumeAmountAndUnit(volumeValue);
		if (typeof amount !== 'number') return 0;
		if (i18n.locale === EN) return stockColTransferUnit(amount);

		// 中文环境：SSE数据以"股"为单位，需要 /100 转换为"手"
		const value = amount / 100;
		return `${value}${unit}`;
	};

	return {
		formattedValue,
		stockColTransferUnit,
		ttmFilter,

		computedClosePrice,
		divideByHundred,
		appendPlusIfPositive,
		formatChangePct,

		getVolumeAmountAndUnit,
		computedVolume,
		computedVolumeForSse
	};
}
